import type { ChatAttachment } from '@/types/domain'

/* What a chat attachment is allowed to become on screen.
 *
 * The file area can afford to be incurious about content type: every row there
 * is a link, and the bytes only ever reach the user through a download they
 * asked for. Chat cannot. A chat bubble RENDERS its attachment inline, which
 * means the content type stops being a label and becomes an instruction —
 * "build an <img> around this", "build a <video> around this".
 *
 * The type is declared by the uploading client and stored as given: the backend
 * defaults an unknown one to application/octet-stream (collab/domain.go) and
 * has no allowlist of its own. So it is attacker-controlled for any user who
 * can upload, and the decision of what element to construct from it must be
 * made against a CLOSED set here rather than by pattern-matching the prefix.
 *
 * The distinction that matters: `image/svg+xml` starts with `image/`. A naive
 * `startsWith('image/')` renders it in an <img>, and an SVG is a document that
 * can carry script. It is deliberately absent from the sets below and lands as
 * a plain file row — downloadable, never rendered.
 */

/** How a bubble should present an attachment. `file` is the safe default and
 *  the only outcome for anything not explicitly listed. */
export type AttachmentKind = 'image' | 'video' | 'audio' | 'file'

/* Exact types only — no wildcards, no prefix matching. Each entry is one the
 * browser decodes as media, chosen because Operations actually sends it: phone
 * photos, a clip of a damaged pallet, a voice note from a driver. */

const IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  // NOT image/svg+xml — see the note above. An SVG is script-capable.
])

const VIDEO_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/ogg',
  // iOS records .mov and reports this; without it every video from an iPhone
  // would fall back to a file row.
  'video/quicktime',
])

const AUDIO_TYPES = new Set([
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  // A WhatsApp-style voice note from Android/iOS.
  'audio/aac',
  'audio/x-m4a',
])

/** Normalises a declared content type for lookup.
 *
 *  A real header carries parameters — `image/jpeg; charset=binary` — and
 *  arbitrary case. Both are stripped before the set is consulted, because a set
 *  miss here means "render as a file", and being wrong in that direction on a
 *  legitimate photo is a bug even though it is not a vulnerability. */
function normalise(contentType: string): string {
  return contentType.split(';')[0]?.trim().toLowerCase() ?? ''
}

/** Decides how an attachment may be rendered.
 *
 *  Returns `file` for anything not on an explicit list — including an empty or
 *  malformed type, and including types the browser could probably display. The
 *  cost of that conservatism is a photo occasionally shown as a row; the cost
 *  of the opposite is executing somebody's upload. */
export function renderableKind(attachment: ChatAttachment): AttachmentKind {
  const type = normalise(attachment.contentType)
  if (IMAGE_TYPES.has(type)) return 'image'
  if (VIDEO_TYPES.has(type)) return 'video'
  if (AUDIO_TYPES.has(type)) return 'audio'
  return 'file'
}

/** Human-readable size. Mirrors the file area's formatting so one file reads
 *  the same in the list and in the bubble. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
